import React, { useState, useEffect } from 'react';
import { useAccount, useReadContract, useReadContracts, useWriteContract, useWaitForTransactionReceipt, usePublicClient } from 'wagmi';
import { parseUnits, formatUnits, isAddress, parseAbi } from 'viem';
import { FaShieldAlt, FaCog, FaPlus, FaTrash, FaExclamationTriangle, FaCheckCircle, FaBalanceScale, FaLock, FaUnlock, FaChartLine, FaSync, FaCoins } from 'react-icons/fa';
import { vaultConfig, CONTRACTS } from '../config/vaultConfig-v2';
import { ADMIN_CONFIG, PROTOCOL_CONFIGS, STRATEGY_PRESETS, isAdmin as checkIsAdmin, validateAllocation, calculateRiskScore } from '../config/adminConfig';
import '../styles/Admin.css';

function Admin() {
  const { address, isConnected } = useAccount();
  const publicClient = usePublicClient();
  const [activeTab, setActiveTab] = useState('adapters');
  const [adapters, setAdapters] = useState([]);
  const [newAdapter, setNewAdapter] = useState({ address: '', name: '', allocation: '' });
  const [newManager, setNewManager] = useState('');
  const [newDepositFee, setNewDepositFee] = useState('');
  const [newWithdrawFee, setNewWithdrawFee] = useState('');
  const [newFeeRecipient, setNewFeeRecipient] = useState('');
  const [rebalanceAllocations, setRebalanceAllocations] = useState({});
  const [isRebalancing, setIsRebalancing] = useState(false);
  const [rebalanceStatus, setRebalanceStatus] = useState('');
  const [isExecutingRebalance, setIsExecutingRebalance] = useState(false);
  const [isHarvesting, setIsHarvesting] = useState(false);
  const [isUpdatingAPR, setIsUpdatingAPR] = useState(false);
  const [currentAPR, setCurrentAPR] = useState(null);

  // Check if user is admin
  const isAdmin = checkIsAdmin(address);
  
  // Get current manager from VaultCore
  const { data: currentManager } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'manager',
    watch: true,
  });
  
  // Check if user is a keeper on VaultManager
  const { data: isKeeper } = useReadContract({
    address: vaultConfig.vaultManager.address,
    abi: parseAbi([
      'function keepers(address) view returns (bool)'
    ]),
    functionName: 'keepers',
    args: [address],
    enabled: !!address,
    watch: true,
  });
  
  // Get owner of VaultManager
  const { data: vaultManagerOwner } = useReadContract({
    address: vaultConfig.vaultManager.address,
    abi: vaultConfig.vaultManager.abi,
    functionName: 'owner',
    watch: true,
  });

  // Get owner of VaultCore for fee management
  const { data: vaultCoreOwner } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: parseAbi([
      'function owner() view returns (address)'
    ]),
    functionName: 'owner',
    watch: true,
  });
  
  // Get performance fee data from VaultManager
  const { data: performanceFeeBps } = useReadContract({
    address: vaultConfig.vaultManager.address,
    abi: vaultConfig.vaultManager.abi,
    functionName: 'performanceFeeBps',
    watch: true,
  });
  
  const { data: lastHarvestTime } = useReadContract({
    address: vaultConfig.vaultManager.address,
    abi: vaultConfig.vaultManager.abi,
    functionName: 'lastHarvestTime',
    watch: true,
  });
  
  const { data: accumulatedPerformanceFees } = useReadContract({
    address: vaultConfig.vaultManager.address,
    abi: parseAbi([
      'function accumulatedPerformanceFees() view returns (uint256)'
    ]),
    functionName: 'accumulatedPerformanceFees',
    watch: true,
  });

  // Deposit and Withdraw Fees from VaultCore
  const { data: depositFeeBps } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'depositFeeBps',
    watch: true,
  });

  const { data: withdrawFeeBps } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'withdrawFeeBps',
    watch: true,
  });

  const { data: feeRecipient } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'feeRecipient',
    watch: true,
  });

  // Get current APR from contract
  const { data: apr } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'getAPR',
    watch: true,
  });

  useEffect(() => {
    if (apr !== undefined) {
      // Convert from basis points to percentage
      setCurrentAPR(Number(apr) / 100);
    }
  }, [apr]);

  // Read vault data from VaultCore and VaultManager
  const { data: owner } = useReadContract({
    address: vaultConfig.vaultManager.address,
    abi: vaultConfig.vaultManager.abi,
    functionName: 'owner',
    enabled: !!address,
  });

  // Get adapters list from VaultCore
  const { data: adaptersList } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'getAdapters',
    watch: true,
  });

  // Get total assets for calculating current percentages
  const { data: totalAssets } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'totalAssets',
    watch: true,
  });

  const { data: emergencyMode } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'emergencyMode',
    watch: true,
  });

  const { data: depositCap } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'maxTotalAssets',
    watch: true,
  });

  const { data: depositCapEnabled } = useReadContract({
    address: vaultConfig.vaultCore.address,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'depositCapEnabled',
    watch: true,
  });

  // Contract write hooks
  const { writeContract: writeVault, data: txHash } = useWriteContract();
  const { isLoading: isTxLoading, isSuccess: isTxSuccess } = useWaitForTransactionReceipt({ hash: txHash });

  // Fetch adapter allocations and balances
  const { data: adapterAllocations, refetch: refetchAllocations } = useReadContracts({
    contracts: adaptersList?.map(addr => ({
      address: vaultConfig.vaultCore.address,
      abi: vaultConfig.vaultCore.abi,
      functionName: 'adapterAllocations',
      args: [addr],
    })) || [],
    enabled: !!adaptersList && adaptersList.length > 0,
    watch: true,
  });

  // Fetch adapter assets
  const { data: adapterAssets } = useReadContracts({
    contracts: adaptersList?.map(addr => ({
      address: addr,
      abi: vaultConfig.adapters.aave.abi,
      functionName: 'totalAssets',
    })) || [],
    enabled: !!adaptersList && adaptersList.length > 0,
    watch: true,
  });
  
  // Refetch allocations when transaction succeeds
  useEffect(() => {
    if (isTxSuccess) {
      console.log('Transaction successful!');
      
      // Reset harvesting state if we were harvesting
      if (isHarvesting) {
        setIsHarvesting(false);
        alert('Performance fees harvested successfully!');
      }
      
      // Refetch allocations if available
      if (refetchAllocations) {
        setTimeout(() => {
          refetchAllocations();
        }, 1000); // Wait a second for chain to update
      }
    }
  }, [isTxSuccess, refetchAllocations, isHarvesting]);

  // Load adapters data with allocations
  useEffect(() => {
    if (adaptersList && adapterAllocations && adapterAssets && totalAssets) {
      console.log('Loading adapter data:', {
        adaptersList,
        adapterAllocations,
        adapterAssets,
        totalAssets: totalAssets.toString()
      });
      
      const loadedAdapters = adaptersList.map((addr, index) => {
        // adapterAllocations returns a tuple: [targetBps, minBps, maxBps]
        const allocationResult = adapterAllocations[index]?.result;
        let targetBps = 0n;
        let minBps = 0n;
        let maxBps = 0n;
        
        if (allocationResult) {
          // Handle both array and object format
          if (Array.isArray(allocationResult)) {
            [targetBps, minBps, maxBps] = allocationResult;
          } else if (allocationResult.targetBps !== undefined) {
            targetBps = allocationResult.targetBps;
            minBps = allocationResult.minBps || 0n;
            maxBps = allocationResult.maxBps || 0n;
          }
        }
        
        const assets = adapterAssets[index]?.result || 0n;
        const currentPercentage = totalAssets > 0n ? (Number(assets) * 100 / Number(totalAssets)) : 0;
        const targetPercentage = Number(targetBps) / 100;
        
        console.log(`Adapter ${index} (${getAdapterName(addr)}):`, {
          address: addr,
          assets: assets.toString(),
          targetBps: targetBps.toString(),
          minBps: minBps.toString(),
          maxBps: maxBps.toString(),
          currentPercentage,
          targetPercentage
        });
        
        return {
          address: addr,
          name: getAdapterName(addr),
          balance: assets,
          currentPercentage,
          targetPercentage,
          targetBps: Number(targetBps),
          minBps: Number(minBps),
          maxBps: Number(maxBps),
        };
      });
      
      setAdapters(loadedAdapters);
      
      // Only initialize rebalance allocations if they haven't been set yet
      // This prevents overwriting user input
      if (Object.keys(rebalanceAllocations).length === 0) {
        const allocations = {};
        loadedAdapters.forEach((adapter, index) => {
          allocations[index] = adapter.targetPercentage.toFixed(1);
        });
        setRebalanceAllocations(allocations);
      }
    }
  }, [adaptersList, adapterAllocations, adapterAssets, totalAssets]);

  const getAdapterName = (address) => {
    const lowerAddr = address.toLowerCase();
    // Use the actual deployed adapter addresses from config
    if (lowerAddr === vaultConfig.adapters.aave.address.toLowerCase()) return 'Aave V3';
    if (lowerAddr === vaultConfig.adapters.euler.address.toLowerCase()) return 'Euler V2';
    if (lowerAddr === vaultConfig.adapters.silo.address.toLowerCase()) return 'Silo Finance';
    if (lowerAddr === vaultConfig.adapters.fortyAcres.address.toLowerCase()) return '40 Acres';
    return `Adapter ${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Harvest performance fees
  const handleHarvestPerformanceFees = async () => {
    if (!isAdmin) return;
    
    setIsHarvesting(true);
    try {
      // Check if we have permissions
      if (!isKeeper && address?.toLowerCase() !== vaultManagerOwner?.toLowerCase()) {
        alert('You need to be a keeper or owner on VaultManager to harvest fees');
        setIsHarvesting(false);
        return;
      }
      
      console.log('Harvesting performance fees...');
      console.log('VaultManager address:', vaultConfig.vaultManager.address);
      console.log('User address:', address);
      console.log('Is keeper:', isKeeper);
      console.log('Is owner:', address?.toLowerCase() === vaultManagerOwner?.toLowerCase());
      
      await writeVault({
        address: vaultConfig.vaultManager.address,
        abi: vaultConfig.vaultManager.abi,
        functionName: 'harvestPerformanceFees',
        args: [], // harvestPerformanceFees takes no arguments
      });
      
      // The success will be handled by the transaction receipt watcher
      console.log('Harvest transaction initiated');
      
    } catch (error) {
      console.error('Error harvesting fees:', error);
      alert(`Failed to harvest fees: ${error.message || 'Unknown error'}`);
      setIsHarvesting(false);
    }
  };

  // Add adapter
  const handleAddAdapter = async () => {
    if (!isAdmin || !newAdapter.address || !isAddress(newAdapter.address)) return;
    
    try {
      await writeVault({
        address: vaultConfig.vaultCore.address,
        abi: vaultConfig.vaultCore.abi,
        functionName: 'addAdapter',
        args: [newAdapter.address],
      });
      
      setNewAdapter({ address: '', name: '', allocation: '' });
    } catch (error) {
      console.error('Error adding adapter:', error);
    }
  };

  // Remove adapter - get index from adapter address
  const handleRemoveAdapter = async (adapterAddress) => {
    if (!isAdmin) return;
    
    try {
      const index = adaptersList.indexOf(adapterAddress);
      if (index === -1) {
        console.error('Adapter not found');
        return;
      }
      
      await writeVault({
        address: vaultConfig.vaultCore.address,
        abi: vaultConfig.vaultCore.abi,
        functionName: 'removeAdapter',
        args: [index],
      });
    } catch (error) {
      console.error('Error removing adapter:', error);
    }
  };

  // Rebalance allocations using VaultCore setAllocation and VaultManager rebalanceToTargetAllocations
  const handleRebalance = async () => {
    if (!isAdmin || !adapters.length) return;
    
    setIsRebalancing(true);
    try {
      // Validate total equals 100%
      const total = Object.values(rebalanceAllocations).reduce((sum, val) => sum + parseFloat(val || 0), 0);
      if (Math.abs(total - 100) > 0.01) {
        alert('Allocations must sum to exactly 100%');
        setIsRebalancing(false);
        return;
      }
      
      console.log('Starting rebalance process...');
      setRebalanceStatus('Setting adapter allocations...');
      
      // Step 1: Update allocations for each adapter using VaultCore (as manager)
      for (let i = 0; i < adapters.length; i++) {
        const adapter = adapters[i];
        const targetPercentage = parseFloat(rebalanceAllocations[i] || 0);
        const targetBps = Math.round(targetPercentage * 100); // Convert to basis points
        
        // Set min/max bounds (±5% from target for flexibility)
        const minBps = Math.max(0, targetBps - 500); // 5% below target
        const maxBps = Math.min(10000, targetBps + 500); // 5% above target
        
        setRebalanceStatus(`Setting allocation for ${adapter.name} (${i + 1}/${adapters.length})...`);
        
        console.log(`Setting allocation for ${adapter.name}:`, {
          address: adapter.address,
          target: `${targetPercentage}% (${targetBps} bps)`,
          min: `${minBps / 100}%`,
          max: `${maxBps / 100}%`
        });
        
        // Call setAdapterAllocation on VaultManager (owner/keeper can call this)
        const tx = await writeVault({
          address: vaultConfig.vaultManager.address,
          abi: vaultConfig.vaultManager.abi,
          functionName: 'setAdapterAllocation',
          args: [adapter.address, targetBps, minBps, maxBps],
        });
        
        console.log(`Allocation set for ${adapter.name}, tx:`, tx);
      }
      
      // Step 2: Check if we have permissions for rebalancing
      if (!isKeeper && address?.toLowerCase() !== vaultManagerOwner?.toLowerCase()) {
        setRebalanceStatus('Warning: You may not have keeper/owner permissions for rebalancing');
        console.warn('User is not a keeper or owner on VaultManager');
      }
      
      // Step 3: Trigger the actual rebalancing on VaultManager
      setRebalanceStatus('Executing rebalance...');
      console.log('All allocations updated. Triggering rebalance...');
      
      const rebalanceTx = await writeVault({
        address: vaultConfig.vaultManager.address,
        abi: vaultConfig.vaultManager.abi,
        functionName: 'rebalanceToTargetAllocations',
      });
      
      console.log('Rebalance transaction submitted:', rebalanceTx);
      setRebalanceStatus('Rebalance completed successfully!');
      alert('Rebalance initiated successfully! Funds will be redistributed according to new allocations.');
      
      // Clear status after 3 seconds
      setTimeout(() => setRebalanceStatus(''), 3000);
      
    } catch (error) {
      console.error('Error during rebalance:', error);
      setRebalanceStatus(`Error: ${error.message || 'Unknown error'}`);
      alert(`Rebalance failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsRebalancing(false);
    }
  };

  // Execute rebalance without changing allocations
  const handleExecuteRebalance = async () => {
    if (!isAdmin) return;
    
    setIsExecutingRebalance(true);
    setRebalanceStatus('Executing rebalance to target allocations...');
    
    try {
      // Check if we have permissions for rebalancing
      if (!isKeeper && address?.toLowerCase() !== vaultManagerOwner?.toLowerCase()) {
        setRebalanceStatus('Error: You need keeper or owner permissions on VaultManager');
        alert('You need to be a keeper or owner on VaultManager to execute rebalancing');
        return;
      }
      
      console.log('Executing rebalance to existing target allocations...');
      
      const rebalanceTx = await writeVault({
        address: vaultConfig.vaultManager.address,
        abi: vaultConfig.vaultManager.abi,
        functionName: 'rebalanceToTargetAllocations',
      });
      
      console.log('Rebalance transaction submitted:', rebalanceTx);
      setRebalanceStatus('Rebalance executed successfully!');
      alert('Rebalance executed! Funds are being redistributed to match target allocations.');
      
      // Clear status after 3 seconds
      setTimeout(() => setRebalanceStatus(''), 3000);
      
    } catch (error) {
      console.error('Error during rebalance:', error);
      setRebalanceStatus(`Error: ${error.message || 'Unknown error'}`);
      alert(`Rebalance failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExecutingRebalance(false);
    }
  };

  // Emergency controls
  const handleEmergencyWithdraw = async (adapterAddress) => {
    if (!isAdmin) return;
    
    try {
      const adapter = adapters.find(a => a.address === adapterAddress);
      const amount = adapter?.balance || 0n;
      
      await writeVault({
        address: vaultConfig.vaultManager.address,
        abi: vaultConfig.vaultManager.abi,
        functionName: 'emergencyWithdrawFromAdapter',
        args: [adapterAddress, amount],
      });
    } catch (error) {
      console.error('Error executing emergency withdrawal:', error);
    }
  };

  const handleToggleEmergencyMode = async () => {
    if (!isAdmin) return;
    
    try {
      await writeVault({
        address: vaultConfig.vaultCore.address,
        abi: vaultConfig.vaultCore.abi,
        functionName: 'setEmergencyMode',
        args: [!emergencyMode],
      });
    } catch (error) {
      console.error('Error toggling emergency mode:', error);
    }
  };

  const handleUpdateDepositCap = async (newCap, enabled) => {
    if (!isAdmin) return;
    
    try {
      await writeVault({
        address: vaultConfig.vaultCore.address,
        abi: vaultConfig.vaultCore.abi,
        functionName: 'setDepositCap',
        args: [parseUnits(newCap, 6), enabled],
      });
    } catch (error) {
      console.error('Error updating deposit cap:', error);
    }
  };

  // Set new manager
  const handleSetManager = async () => {
    if (!isAdmin || !newManager || !isAddress(newManager)) return;
    
    try {
      console.log('Setting new manager:', newManager);
      await writeVault({
        address: vaultConfig.vaultCore.address,
        abi: vaultConfig.vaultCore.abi,
        functionName: 'setManager',
        args: [newManager],
      });
      alert(`Manager updated successfully to ${newManager}`);
      setNewManager('');
    } catch (error) {
      console.error('Error setting manager:', error);
      alert(`Failed to set manager: ${error.message || 'Unknown error'}`);
    }
  };

  if (!isConnected) {
    return (
      <div className="admin-container">
        <div className="admin-warning">
          <FaLock size={48} />
          <h2>Admin Access Required</h2>
          <p>Please connect your wallet to access admin functions</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-container">
        <div className="admin-warning">
          <FaExclamationTriangle size={48} />
          <h2>Unauthorized Access</h2>
          <p>You do not have admin privileges</p>
          <p className="admin-address">Connected: {address}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <FaShieldAlt className="admin-icon" />
        <h1>Vault Admin Panel</h1>
        <div className="admin-status">
          <span className="status-badge success">
            <FaCheckCircle /> Admin Connected
          </span>
          {emergencyMode && (
            <span className="status-badge warning">
              <FaExclamationTriangle /> Emergency Mode Active
            </span>
          )}
        </div>
      </div>

      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'adapters' ? 'active' : ''}`}
          onClick={() => setActiveTab('adapters')}
        >
          <FaCog /> Adapters
        </button>
        <button
          className={`admin-tab ${activeTab === 'rebalance' ? 'active' : ''}`}
          onClick={() => setActiveTab('rebalance')}
        >
          <FaBalanceScale /> Rebalance
        </button>
        <button
          className={`admin-tab ${activeTab === 'apr' ? 'active' : ''}`}
          onClick={() => setActiveTab('apr')}
        >
          <FaChartLine /> APR
        </button>
        <button
          className={`admin-tab ${activeTab === 'fees' ? 'active' : ''}`}
          onClick={() => setActiveTab('fees')}
        >
          <FaCoins /> Fees
        </button>
        <button
          className={`admin-tab ${activeTab === 'keeper' ? 'active' : ''}`}
          onClick={() => setActiveTab('keeper')}
        >
          <FaShieldAlt /> Keeper
        </button>
        <button
          className={`admin-tab ${activeTab === 'manager' ? 'active' : ''}`}
          onClick={() => setActiveTab('manager')}
        >
          <FaCog /> Manager
        </button>
        <button
          className={`admin-tab ${activeTab === 'emergency' ? 'active' : ''}`}
          onClick={() => setActiveTab('emergency')}
        >
          <FaExclamationTriangle /> Emergency
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'adapters' && (
          <div className="adapters-section">
            <h2>Manage Adapters</h2>
            <div className="adapters-list">
              {adapters.map((adapter, index) => (
                <div key={adapter.address} className="adapter-item">
                  <div className="adapter-info">
                    <span className="adapter-name">{adapter.name}</span>
                    <span className="adapter-address">{adapter.address}</span>
                    <span className="adapter-balance">
                      ${formatUnits(adapter.balance, 6)} ({(adapter.currentPercentage || adapter.percentage || 0).toFixed(1)}%)
                    </span>
                  </div>
                  <button
                    className="remove-adapter"
                    onClick={() => handleRemoveAdapter(adapter.address)}
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>
            <div className="add-adapter">
              <h3>Add New Adapter</h3>
              <div className="form-group">
                <input
                  type="text"
                  value={newAdapter.address}
                  onChange={(e) => setNewAdapter(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Adapter address (0x...)"
                />
              </div>
              <button
                className="add-button"
                onClick={handleAddAdapter}
                disabled={!isAddress(newAdapter.address)}
              >
                <FaPlus /> Add Adapter
              </button>
            </div>
          </div>
        )}

        {activeTab === 'rebalance' && (
          <div className="rebalance-section">
            <h2>Rebalance Allocations</h2>
            <div className="allocation-controls">
              {adapters.map((adapter, index) => (
                <div key={adapter.address} className="allocation-item">
                  <div className="allocation-info">
                    <span className="adapter-name">{adapter.name}</span>
                    <div className="allocation-details">
                      <span className="current-allocation">
                        Current: {adapter.currentPercentage?.toFixed(1) || '0.0'}%
                      </span>
                      <span className="target-allocation">
                        Target: {adapter.targetPercentage?.toFixed(1) || '0.0'}%
                      </span>
                      <span className="allocation-range">
                        Range: {((adapter.minBps || 0) / 100).toFixed(1)}% - {((adapter.maxBps || 0) / 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="allocation-input">
                    <label>New Target:</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={rebalanceAllocations[index] || '0'}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        if (value >= 0 && value <= 100) {
                          setRebalanceAllocations(prev => ({
                            ...prev,
                            [index]: e.target.value,
                          }));
                        }
                      }}
                    />
                    <span>%</span>
                  </div>
                </div>
              ))}
              <div className="allocation-total">
                <span>Total Allocation:</span>
                <span className={`total-percentage ${
                  Object.values(rebalanceAllocations).reduce((sum, val) => sum + parseFloat(val || 0), 0).toFixed(1) == 100 
                    ? 'valid' 
                    : 'invalid'
                }`}>
                  {Object.values(rebalanceAllocations).reduce((sum, val) => sum + parseFloat(val || 0), 0).toFixed(1)}%
                </span>
              </div>
              
              {/* Check if allocations are out of balance */}
              {(() => {
                const isOutOfBalance = adapters.some(adapter => 
                  Math.abs(adapter.currentPercentage - adapter.targetPercentage) > 1
                );
                
                return isOutOfBalance ? (
                  <div className="out-of-balance-notice">
                    <span className="warning-icon">⚠️</span>
                    <span>Allocations are out of balance. Current targets are already set - you can rebalance now.</span>
                  </div>
                ) : null;
              })()}
              
              <div className="rebalance-buttons">
                {/* Button to execute rebalance with existing targets */}
                {adapters.some(adapter => Math.abs(adapter.currentPercentage - adapter.targetPercentage) > 1) && (
                  <button
                    className="execute-rebalance-button"
                    onClick={handleExecuteRebalance}
                    disabled={isExecutingRebalance}
                  >
                    {isExecutingRebalance ? 'Executing...' : '⚡ Rebalance to Current Targets'}
                  </button>
                )}
                
                {/* Button to update allocations and rebalance */}
                <button
                  className="rebalance-button"
                  onClick={handleRebalance}
                  disabled={
                    isRebalancing ||
                    Object.values(rebalanceAllocations).reduce((sum, val) => sum + parseFloat(val || 0), 0).toFixed(1) != 100
                  }
                >
                  {isRebalancing ? 'Updating & Rebalancing...' : '📝 Update Targets & Rebalance'}
                </button>
              </div>
              {rebalanceStatus && (
                <div className={`rebalance-status ${rebalanceStatus.includes('Error') ? 'error' : rebalanceStatus.includes('completed') ? 'success' : 'info'}`}>
                  {rebalanceStatus}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'apr' && (
          <div className="apr-section">
            <h2>APR Management</h2>
            <div className="apr-controls">
              <div className="apr-status">
                <h3>Current Vault APR</h3>
                <div className="apr-display">
                  <span className="apr-value">
                    {currentAPR !== null ? `${currentAPR.toFixed(2)}%` : 'Loading...'}
                  </span>
                  <p className="apr-note">
                    This APR is calculated from all adapter performances
                  </p>
                </div>
              </div>
              
              <div className="apr-update">
                <h3>Update APR</h3>
                <p className="update-description">
                  Recalculate the vault's APR based on current adapter performance.
                  This will fetch the latest yields from all adapters and update the displayed APR.
                </p>
                <button
                  className="update-apr-button"
                  onClick={async () => {
                    if (!isAdmin) return;
                    setIsUpdatingAPR(true);
                    try {
                      await writeVault({
                        address: vaultConfig.vaultCore.address,
                        abi: vaultConfig.vaultCore.abi,
                        functionName: 'updateAPR',
                      });
                      console.log('APR updated successfully');
                    } catch (error) {
                      console.error('Error updating APR:', error);
                    } finally {
                      setIsUpdatingAPR(false);
                    }
                  }}
                  disabled={isUpdatingAPR || !isAdmin}
                >
                  {isUpdatingAPR ? (
                    <><FaSync className="spinning" /> Updating...</>
                  ) : (
                    <><FaSync /> Update APR Now</>
                  )}
                </button>
              </div>
              
              <div className="apr-info">
                <h3>How APR Works</h3>
                <ul>
                  <li>APR is calculated from real-time adapter performance</li>
                  <li>Updates aggregate yields from Aave, Euler, 40 Acres, and Silo</li>
                  <li>Weighted by current allocation percentages</li>
                  <li>Displayed on the main vault page for users</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'fees' && (
          <div className="fees-section">
            <h2>Fees Management</h2>
            <div className="fees-controls">
              {/* Deposit/Withdraw Fees Section */}
              <div className="fee-status">
                <h3>Deposit & Withdraw Fees</h3>
                <div className="fee-info-grid">
                  <div className="fee-info-item">
                    <span className="label">Deposit Fee:</span>
                    <span className="value">
                      {depositFeeBps !== undefined ? `${(Number(depositFeeBps) / 100).toFixed(2)}%` : '0.00%'}
                    </span>
                  </div>
                  <div className="fee-info-item">
                    <span className="label">Withdraw Fee:</span>
                    <span className="value">
                      {withdrawFeeBps !== undefined ? `${(Number(withdrawFeeBps) / 100).toFixed(2)}%` : '0.00%'}
                    </span>
                  </div>
                  <div className="fee-info-item">
                    <span className="label">Fee Recipient:</span>
                    <span className="value" style={{ fontSize: '0.85em' }}>
                      {feeRecipient ? `${feeRecipient.slice(0, 6)}...${feeRecipient.slice(-4)}` : 'Not set'}
                    </span>
                  </div>
                </div>
                
                <div className="fee-update-form">
                  <h4>Update Fees</h4>
                  <div className="form-group">
                    <label>Deposit Fee (%):</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="10"
                      value={newDepositFee}
                      onChange={(e) => setNewDepositFee(e.target.value)}
                      placeholder={depositFeeBps !== undefined ? (Number(depositFeeBps) / 100).toFixed(2) : '0'}
                    />
                  </div>
                  <div className="form-group">
                    <label>Withdraw Fee (%):</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="10"
                      value={newWithdrawFee}
                      onChange={(e) => setNewWithdrawFee(e.target.value)}
                      placeholder={withdrawFeeBps !== undefined ? (Number(withdrawFeeBps) / 100).toFixed(2) : '0'}
                    />
                  </div>
                  <div className="form-group">
                    <label>Fee Recipient:</label>
                    <input
                      type="text"
                      value={newFeeRecipient}
                      onChange={(e) => setNewFeeRecipient(e.target.value)}
                      placeholder={feeRecipient || 'Enter address'}
                    />
                  </div>
                  <button
                    className="update-fees-button"
                    onClick={() => {
                      const depositBps = newDepositFee ? Math.floor(parseFloat(newDepositFee) * 100) : Number(depositFeeBps || 0);
                      const withdrawBps = newWithdrawFee ? Math.floor(parseFloat(newWithdrawFee) * 100) : Number(withdrawFeeBps || 0);
                      const recipient = newFeeRecipient || feeRecipient;
                      
                      if (!recipient || !recipient.startsWith('0x')) {
                        alert('Please enter a valid fee recipient address');
                        return;
                      }
                      
                      writeVault({
                        address: vaultConfig.vaultCore.address,
                        abi: vaultConfig.vaultCore.abi,
                        functionName: 'setFees',
                        args: [depositBps, withdrawBps, recipient],
                      });
                    }}
                    disabled={address?.toLowerCase() !== vaultCoreOwner?.toLowerCase()}
                  >
                    Update Fees
                  </button>
                  {address?.toLowerCase() !== vaultCoreOwner?.toLowerCase() && (
                    <p className="permission-warning">
                      ⚠️ You need to be the owner of VaultCore to update fees
                    </p>
                  )}
                </div>
              </div>

              {/* Performance Fees Section */}
              <div className="fee-status">
                <h3>Performance Fees</h3>
                <div className="fee-info-grid">
                  <div className="fee-info-item">
                    <span className="label">Performance Fee:</span>
                    <span className="value">
                      {performanceFeeBps ? `${(Number(performanceFeeBps) / 100).toFixed(2)}%` : '0.00%'}
                    </span>
                  </div>
                  <div className="fee-info-item">
                    <span className="label">Last Harvest:</span>
                    <span className="value">
                      {lastHarvestTime ? new Date(Number(lastHarvestTime) * 1000).toLocaleString() : 'Never'}
                    </span>
                  </div>
                  <div className="fee-info-item">
                    <span className="label">Accumulated Fees:</span>
                    <span className="value">
                      ${accumulatedPerformanceFees ? formatUnits(accumulatedPerformanceFees, 6) : '0.00'} USDC
                    </span>
                  </div>
                  <div className="fee-info-item">
                    <span className="label">Time Since Harvest:</span>
                    <span className="value">
                      {lastHarvestTime ? 
                        `${Math.floor((Date.now() / 1000 - Number(lastHarvestTime)) / 3600)} hours` : 
                        'N/A'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="harvest-fees">
                <h3>Harvest Performance Fees</h3>
                <p className="harvest-description">
                  Harvest accumulated performance fees and distribute them to the fee recipient.
                  This will calculate profits since the last harvest and mint fee shares accordingly.
                </p>
                <button
                  className="harvest-button"
                  onClick={handleHarvestPerformanceFees}
                  disabled={isHarvesting || (!isKeeper && address?.toLowerCase() !== vaultManagerOwner?.toLowerCase())}
                >
                  {isHarvesting ? (
                    <><FaSync className="spinning" /> Harvesting...</>
                  ) : (
                    <><FaCoins /> Harvest Fees Now</>
                  )}
                </button>
                {(!isKeeper && address?.toLowerCase() !== vaultManagerOwner?.toLowerCase()) && (
                  <p className="permission-warning">
                    ⚠️ You need to be a keeper or owner on VaultManager to harvest fees
                  </p>
                )}
              </div>
              
              <div className="fee-info">
                <h3>How Performance Fees Work</h3>
                <ul>
                  <li>Performance fees are charged on profits generated by the vault</li>
                  <li>Fees are calculated as a percentage of the yield earned</li>
                  <li>Harvesting mints new shares to the fee recipient</li>
                  <li>No fees are charged if the vault hasn't generated profits</li>
                  <li>Fees help maintain and improve the vault infrastructure</li>
                </ul>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'keeper' && (
          <div className="keeper-section">
            <h2>Keeper & Permissions</h2>
            <div className="keeper-controls">
              <div className="permissions-status">
                <h3>Your Permissions</h3>
                <div className="permission-list">
                  <div className="permission-item">
                    <span className="permission-label">VaultCore Manager:</span>
                    <span className={`permission-value ${currentManager?.toLowerCase() === address?.toLowerCase() ? 'active' : 'inactive'}`}>
                      {currentManager?.toLowerCase() === address?.toLowerCase() ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                  <div className="permission-item">
                    <span className="permission-label">VaultManager Owner:</span>
                    <span className={`permission-value ${vaultManagerOwner?.toLowerCase() === address?.toLowerCase() ? 'active' : 'inactive'}`}>
                      {vaultManagerOwner?.toLowerCase() === address?.toLowerCase() ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                  <div className="permission-item">
                    <span className="permission-label">VaultManager Keeper:</span>
                    <span className={`permission-value ${isKeeper ? 'active' : 'inactive'}`}>
                      {isKeeper ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                </div>
                <p className="permission-note">
                  To execute rebalancing, you need to be either a Keeper or Owner on VaultManager.
                </p>
              </div>
              
              {vaultManagerOwner?.toLowerCase() === address?.toLowerCase() && (
                <div className="set-keeper">
                  <h3>Manage Keepers</h3>
                  <p>As the VaultManager owner, you can add or remove keepers.</p>
                  <div className="form-group">
                    <label>Add Keeper Address</label>
                    <input
                      type="text"
                      placeholder="0x..."
                      id="newKeeperAddress"
                      className="keeper-input"
                    />
                    <button
                      className="add-keeper-button"
                      onClick={async () => {
                        const input = document.getElementById('newKeeperAddress');
                        const keeperAddress = input.value;
                        if (!isAddress(keeperAddress)) {
                          alert('Invalid address');
                          return;
                        }
                        try {
                          await writeVault({
                            address: vaultConfig.vaultManager.address,
                            abi: parseAbi([
                              'function setKeeper(address keeper, bool status) external'
                            ]),
                            functionName: 'setKeeper',
                            args: [keeperAddress, true],
                          });
                          alert(`Keeper added: ${keeperAddress}`);
                          input.value = '';
                        } catch (error) {
                          console.error('Error adding keeper:', error);
                          alert(`Failed to add keeper: ${error.message}`);
                        }
                      }}
                    >
                      Add Keeper
                    </button>
                  </div>
                  
                  {!isKeeper && (
                    <div className="self-keeper">
                      <button
                        className="add-self-keeper-button"
                        onClick={async () => {
                          try {
                            await writeVault({
                              address: vaultConfig.vaultManager.address,
                              abi: parseAbi([
                                'function setKeeper(address keeper, bool status) external'
                              ]),
                              functionName: 'setKeeper',
                              args: [address, true],
                            });
                            alert('You have been added as a keeper');
                          } catch (error) {
                            console.error('Error adding self as keeper:', error);
                            alert(`Failed to add yourself as keeper: ${error.message}`);
                          }
                        }}
                      >
                        Add Yourself as Keeper
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        
        {activeTab === 'manager' && (
          <div className="manager-section">
            <h2>Manager Settings</h2>
            <div className="manager-controls">
              <div className="current-manager">
                <h3>Current Manager</h3>
                <div className="manager-info">
                  <span className="label">Address:</span>
                  <span className="manager-address">
                    {currentManager || 'Loading...'}
                  </span>
                  {currentManager && currentManager.toLowerCase() === address?.toLowerCase() && (
                    <span className="is-you">(You)</span>
                  )}
                </div>
                <p className="manager-description">
                  The manager has administrative privileges to manage adapters, set allocations, 
                  trigger rebalancing, and update vault parameters.
                </p>
              </div>
              
              <div className="set-manager">
                <h3>Transfer Manager Role</h3>
                <p className="warning-text">
                  ⚠️ Warning: This action will transfer all administrative privileges to the new address. 
                  Make sure you have the correct address before proceeding.
                </p>
                <div className="form-group">
                  <label htmlFor="newManager">New Manager Address</label>
                  <input
                    type="text"
                    id="newManager"
                    value={newManager}
                    onChange={(e) => setNewManager(e.target.value)}
                    placeholder="0x..."
                    className="manager-input"
                  />
                  {newManager && !isAddress(newManager) && (
                    <span className="error-text">Invalid address format</span>
                  )}
                  {newManager && isAddress(newManager) && newManager.toLowerCase() === currentManager?.toLowerCase() && (
                    <span className="error-text">This is already the current manager</span>
                  )}
                </div>
                <button
                  className="set-manager-button"
                  onClick={handleSetManager}
                  disabled={
                    !isAdmin || 
                    !newManager || 
                    !isAddress(newManager) || 
                    newManager.toLowerCase() === currentManager?.toLowerCase()
                  }
                >
                  <FaCog /> Set New Manager
                </button>
              </div>
              
              <div className="manager-permissions">
                <h3>Manager Permissions</h3>
                <ul>
                  <li>Add and remove adapters</li>
                  <li>Set adapter allocations</li>
                  <li>Trigger rebalancing</li>
                  <li>Update deposit caps</li>
                  <li>Set vault fees</li>
                  <li>Enable/disable emergency mode</li>
                  <li>Update APR calculations</li>
                  <li>Transfer manager role</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'emergency' && (
          <div className="emergency-section">
            <h2>Emergency Controls</h2>
            <div className="emergency-controls">
              <div className="emergency-mode">
                <h3>Emergency Mode</h3>
                <p>Current Status: {emergencyMode ? 'ACTIVE' : 'Inactive'}</p>
                <button
                  className={`emergency-toggle ${emergencyMode ? 'deactivate' : 'activate'}`}
                  onClick={handleToggleEmergencyMode}
                >
                  {emergencyMode ? (
                    <>
                      <FaUnlock /> Deactivate Emergency Mode
                    </>
                  ) : (
                    <>
                      <FaLock /> Activate Emergency Mode
                    </>
                  )}
                </button>
              </div>

              <div className="emergency-withdrawals">
                <h3>Emergency Withdrawals</h3>
                <p>Immediately withdraw all funds from an adapter</p>
                {adapters.map((adapter) => (
                  <div key={adapter.address} className="emergency-adapter">
                    <span>{adapter.name}</span>
                    <span>${formatUnits(adapter.balance, 6)}</span>
                    <button
                      className="emergency-withdraw"
                      onClick={() => handleEmergencyWithdraw(adapter.address)}
                    >
                      Emergency Withdraw
                    </button>
                  </div>
                ))}
              </div>

              <div className="deposit-cap-control">
                <h3>Deposit Cap</h3>
                <p>Current Cap: ${depositCap ? formatUnits(depositCap, 6) : '0'}</p>
                <p>Status: {depositCapEnabled ? 'Enabled' : 'Disabled'}</p>
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="New cap in USDC"
                    id="newDepositCap"
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('newDepositCap');
                      handleUpdateDepositCap(input.value, true);
                    }}
                  >
                    Update Cap
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;